import { useEffect, useRef, useState } from 'react';
import type { Message } from '../../api/types';
import { recipientsOf } from '../../lib/emailRecipients';

/**
 * Inbound mail is rendered as html, in a sandboxed iframe.
 *
 * The server hands us `htmlContent.sanitized` — scripts, frames, forms and
 * handlers already stripped — but the mail still carries arbitrary css that would
 * bleed into the console (and inherit from it) if it were inlined. The frame is
 * the isolation: no `allow-same-origin`, so it lands in a null origin and cannot
 * reach this document, its storage or its cookies whatever the html turns out to
 * hold. Scripts are enabled only so the injected reporter below can measure the
 * rendered height, which is the one thing the parent cannot read across origins.
 */
const MIN_HEIGHT = 32;
const MAX_HEIGHT = 4000;

const frameDocument = (html: string) => `<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; background: transparent; }
  /* The frame is sized to its content, so a vertical scrollbar can only mean the
     measurement was a pixel short — and it would sit inside the bubble. */
  html { overflow-y: hidden; }
  body { font: 13.5px/1.5 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a1a24; }
  /* Mail is authored for a 600-700px canvas; let it scroll sideways inside the
     bubble rather than force the bubble wide enough to break the thread layout. */
  body { overflow-x: auto; }
  body > * { max-width: 100%; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: #0073e6; }
</style></head>
<body>${html}
<script>
  var report = function () {
    // document.documentElement is clamped to the frame's viewport, so measuring it
    // locks in whatever height the parent last set and can never shrink back — an
    // early measurement taken before layout settles would strand the bubble at it.
    // The body box is content-driven.
    // A frame laid out at zero width (a hidden pane, a thread rendered before it
    // is shown) wraps every line to nothing and measures several times too tall.
    // Skip it — the observer below re-measures once the frame has real width.
    if (!window.innerWidth) return;
    var body = document.body;
    var height = Math.max(body.scrollHeight, Math.ceil(body.getBoundingClientRect().height));
    // A horizontal scrollbar lives inside the frame and would eat the last line;
    // the 2px covers subpixel rounding, which is the whole reason a frame sized
    // to its own content would show a scrollbar at all.
    var scrollbar = body.scrollWidth > body.clientWidth ? 18 : 0;
    parent.postMessage({ type: 'email-frame-height', height: height + scrollbar + 2 }, '*');
  };
  report();
  // Remote images have no reserved space until they arrive.
  window.addEventListener('load', report);
  setTimeout(report, 400);
  new ResizeObserver(report).observe(document.body);
</script>
</body></html>`;

/**
 * A block of mail html, isolated. Exported because a campaign body is the same
 * problem — operator-authored html with its own css, rendered inside the console
 * — and it is not sanitized server-side at all on that path, so the null origin
 * is doing more work there than it is here.
 */
export function EmailFrame({ html, title }: { html: string; title: string }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(MIN_HEIGHT);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The frame has no origin to check, so identity is the window itself.
      if (event.source !== frame.current?.contentWindow) return;
      const data = event.data as { type?: string; height?: number };
      if (data?.type !== 'email-frame-height' || typeof data.height !== 'number') return;
      setHeight(Math.min(Math.max(Math.ceil(data.height), MIN_HEIGHT), MAX_HEIGHT));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <iframe
      ref={frame}
      className="msg-email-frame"
      title={title}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      srcDoc={frameDocument(html)}
      style={{ height }}
    />
  );
}

/**
 * Who a mail in the thread was actually addressed to.
 *
 * Worth a line of its own because a shared inbox routinely holds mail that was
 * not written to it alone — a booking confirmation copied to the client's
 * assistant, an operations thread with three offices on it — and until this
 * existed the console showed one contact name over a message four people can
 * read. An agent replying without knowing that is the failure it prevents.
 *
 * Collapsed to the first name plus a count past two addresses: the header
 * belongs above the body, not instead of it. Bcc is shown only on our OWN
 * messages — an inbound blind copy is stripped before it arrives, so anything
 * there was addressed to this mailbox, while an outgoing one is a fact the
 * sender needs back (who else got this, invisibly, when the customer replies).
 */
const SHOWN_COLLAPSED = 2;

function Row({ label, values }: { label: string; values: string[] }) {
  if (!values.length) return null;
  return (
    <span className="msg-email-to-row">
      <span className="msg-email-to-label">{label}</span>
      {values.join(', ')}
    </span>
  );
}

export function EmailRecipients({
  message,
  fallbackTo,
}: {
  message: Message;
  /** The contact's own address — what an outgoing reply went to when it named
   *  no recipients of its own, which is every ordinary reply. */
  fallbackTo?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const { to, cc, bcc } = recipientsOf(message, fallbackTo);
  const outgoing = message.messageType !== 'incoming';
  const blind = outgoing ? bcc : [];
  const total = to.length + cc.length + blind.length;
  if (!total) return null;

  if (!open && total > SHOWN_COLLAPSED) {
    const shown = [...to, ...cc, ...blind].slice(0, SHOWN_COLLAPSED);
    return (
      <button
        type="button"
        className="msg-email-to is-collapsed"
        onClick={() => setOpen(true)}
        title="Show every recipient"
      >
        <span className="msg-email-to-label">To</span>
        {shown.join(', ')}
        <span className="msg-email-to-more">+{total - shown.length}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="msg-email-to"
      onClick={() => setOpen(false)}
      title={total > SHOWN_COLLAPSED ? 'Collapse' : undefined}
    >
      <Row label="To" values={to} />
      <Row label="Cc" values={cc} />
      <Row label="Bcc" values={blind} />
    </button>
  );
}

export function EmailBody({ message }: { message: Message }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const html = message.contentAttributes.email?.htmlContent;

  // Text-only mail, mail too large to sanitize, and anything ingested before the
  // server started storing sanitized html: the plain-text rendering is the body.
  // Older rows can hold a blank content too (the parser once let a whitespace
  // text/plain part win) — the subject beats an empty bubble until they are
  // backfilled.
  if (!html?.sanitized) {
    const email = message.contentAttributes.email;
    const text = message.content?.trim() || email?.subject;
    return text ? <div className="msg-email-plain">{text}</div> : null;
  }

  return (
    <div className="msg-email">
      <EmailFrame html={html.sanitized} title="Email message" />
      {html.sanitizedQuoted && (
        <>
          <button
            type="button"
            className="msg-email-quoted"
            onClick={() => setShowQuoted((shown) => !shown)}
            title={showQuoted ? 'Hide trimmed content' : 'Show trimmed content'}
          >
            •••
          </button>
          {showQuoted && <EmailFrame html={html.sanitizedQuoted} title="Trimmed content" />}
        </>
      )}
    </div>
  );
}
